import { ApplianceModel, ColorModel, Filter, FilterOption } from '@common/models';
import { convertMapToRecord } from '@common/utils';

export type ColorFilters = Record<string, Filter>;

type FilterModel = ApplianceModel | ColorModel;

interface BuildFilters {
    filters: Map<string, Filter>;
    filterOptions: Map<string, Map<string, number>>;
}

export const buildFilters = (entries: FilterModel[]): BuildFilters => {
    const filters = new Map<string, Filter>();
    const filterOptions = new Map<string, Map<string, number>>();

    entries.forEach((entry) => {
        entry.features.forEach((feature) => {
            filters.set(feature.description, { filterOptions: [], isExpanded: true, position: feature.position });
            const currentFilterOption = filterOptions.get(feature.description);

            if (currentFilterOption) {
                const numberOfItems = currentFilterOption.get(feature.value);
                if (numberOfItems) {
                    currentFilterOption.set(feature.value, numberOfItems + 1);
                } else {
                    currentFilterOption.set(feature.value, 1);
                }
                filterOptions.set(feature.description, currentFilterOption);
            } else {
                // add a filter option: Polished, Non-Porous
                const filterOption = new Map<string, number>();

                // initialize the filter option with 1 item, i.e:
                // Polished: 1
                // Non-Porous: 1
                filterOption.set(feature.value, 1);

                // then set the 'filterOption' to the 'filterOptions'
                filterOptions.set(feature.description, filterOption);
            }
        });
    });

    return {
        filters,
        filterOptions,
    };
};

/**
 * Build new filters. May use prevFilters to keep selections.
 */
export const generateFilters = <T extends FilterModel>(entries: T[], prevFilters: ColorFilters = {}): ColorFilters => {
    // build filters from new entries
    const { filters, filterOptions } = buildFilters(entries);
    const prevMap = new Map<string, Filter>(Object.entries(prevFilters));

    const selectedOptions = Object.entries(prevFilters).reduce((acc, [key, filter]) => {
        return acc.set(key, {
            ...filter,
            filterOptions: filter.filterOptions.filter((option) => option.isSelected),
        });
    }, new Map<string, Filter>());

    // Push the 'filterOptions' into the 'filters' parent map
    for (const [key, value] of [...filterOptions.entries()]) {
        const options = [...value.entries()];
        const filterOptions: FilterOption[] = options.map((val) => {
            return {
                name: val[0],
                numberOfItems: val[1],
                isSelected:
                    selectedOptions
                        .get(key)
                        ?.filterOptions.map((option) => option.name)
                        .includes(val[0]) ?? false,
            };
        });
        const prevFilter = prevMap.get(key);
        const currFilter = filters.get(key);
        filters.set(key, {
            filterOptions: filterOptions,
            isExpanded: prevFilter?.isExpanded ?? currFilter?.isExpanded ?? true,
            position: currFilter?.position ?? 0,
        });
    }

    return convertMapToRecord(filters);
};

/**
 * @deprecated use futureInitializeFiltersOptions instead drop setFilters and use the returned value
 */
export function initializeFiltersOptions<T extends FilterModel>(
    entries: T[],
    setFilters: (filters: ColorFilters) => void,
    prevFilters: ColorFilters = {}
): void {
    const { filters, filterOptions } = buildFilters(entries);

    const selectedOptions = Object.entries(prevFilters).reduce((acc, [key, filter]) => {
        return acc.set(key, {
            ...filter,
            filterOptions: filter.filterOptions.filter((option) => option.isSelected),
        });
    }, new Map<string, Filter>());

    // Push the 'filterOptions' into the 'filters' parent map
    for (const [key, value] of [...filterOptions.entries()]) {
        const options = [...value.entries()];
        const filterOptions: FilterOption[] = options.map((val) => {
            return {
                name: val[0],
                numberOfItems: val[1],
                isSelected:
                    selectedOptions
                        .get(key)
                        ?.filterOptions.map((option) => option.name)
                        .includes(val[0]) ?? false,
            };
        });
        const currFilter = filters.get(key);
        filters.set(key, { filterOptions: filterOptions, isExpanded: true, position: currFilter?.position ?? 0 });
    }

    setFilters(convertMapToRecord(filters));
}

export const getSelectedFiltersMap = (filters: ColorFilters): Map<string, FilterOption[]> => {
    // this will hold the selected filters
    const selectedFilters = new Map<string, FilterOption[]>();

    // populate the map which contains just the selected filters
    for (const key of Object.keys(filters)) {
        const currentFilters = filters[key];
        if (currentFilters) {
            selectedFilters.set(
                key,
                currentFilters.filterOptions.filter((option) => option.isSelected)
            );
        }
    }

    return selectedFilters;
};

const transformSelectedFiltersRecordToMap = (filters: ColorFilters): Map<string, Filter> => {
    // this will hold the selected filters
    const filtersMap = new Map<string, Filter>();

    // transform selected filters from record to map
    for (const key of Object.keys(filters)) {
        const currentFilters = filters[key];
        if (currentFilters) {
            filtersMap.set(key, currentFilters);
        }
    }

    return filtersMap;
};

type FilterEntry = [string, FilterOption[]];

/**
 * Filters a list of elements based on the selected filter criteria.
 * The method returns elements that match the specified filter conditions.
 *
 * @param {FilterEntry[]} selectedFiltersArray - An array of key-value pairs where the key represents the filter category and the value is an array of filter options.
 * @param {T[]} browsedElements - An array of elements to be filtered. Each element must conform to the structure defined by the `FilterModel` generic type.
 * @param {boolean} andEverything - A flag indicating whether to do an "AND" for all filter options or an "OR" operation.
 *  if true, and Color [Black, Gold], then it will return the elements that have both colors.
 *  if false, and Color [Black, Gold], then it will return the elements that have either color.
 * @return {T[]} A filtered array of elements that match the provided filter criteria.
 */
export function filterAllEntries<T extends FilterModel>(
    selectedFiltersArray: FilterEntry[],
    browsedElements: T[],
    andEverything = false
): T[] {
    // restructure the filers array based on whether we're doing "AND" for all operations
    // // Input:
    // selectedFiltersArray = [
    //   ["color", ["red", "blue"]],
    //   ["size", ["large"]]
    // ]
    //
    // // With andEverything = true:
    // filters = [
    //   ["color", ["red"]],
    //   ["color", ["blue"]],
    //   ["size", ["large"]]
    // ]
    //
    // // With andEverything = false:
    // filters = [
    //   ["color", ["red", "blue"]],
    //   ["size", ["large"]]
    // ]
    const filters = selectedFiltersArray
        .filter(([_key, value]) => value.length > 0)
        .reduce((acc, [key, values]) => {
            return [
                ...acc,
                ...(andEverything
                    ? values.map((val) => [key, [val]] as FilterEntry)
                    : ([[key, values]] as FilterEntry[])),
            ];
        }, [] as FilterEntry[]);

    return filters.reduce((products, [filterKey, filterOptions]) => {
        return products.filter((product: T) =>
            product.features.some(
                (feature) =>
                    feature.description === filterKey &&
                    filterOptions.some((filter: FilterOption) => filter.name === feature.value)
            )
        );
    }, browsedElements);
}

export function updateFiltersOptions<T extends FilterModel>(
    filters: ColorFilters,
    browsedEntries: T[],
    callback?: (updatedFilters: ColorFilters) => void
): void {
    const clonedFilters = structuredClone(filters);

    // this will hold the selected filters
    const selectedFilters = getSelectedFiltersMap(clonedFilters);

    // represents the filters but transformed from Record to Map
    const filtersMap = transformSelectedFiltersRecordToMap(clonedFilters);

    // iterate over the latest state of the filters
    for (const [filterKey, filterValue] of Array.from(filtersMap.entries())) {
        // get all the filter options for the current filter key
        const filterOptions = selectedFilters.get(filterKey);

        // remove it from the selected filters because a filter option
        // have effect only for the other sections of filters (e.g. choosing white color will not remove
        // the black color from the filters)
        selectedFilters.delete(filterKey);

        // transform the selected filters into an array
        const selectedFiltersArray = Array.from(selectedFilters.entries());

        // after we get the array with the selected filters then add back the deleted filters into the map
        if (filterOptions) {
            selectedFilters.set(filterKey, filterOptions);
        }

        const allEntries = filterAllEntries(selectedFiltersArray, browsedEntries);
        // get the filters
        const filters = filterValue.filterOptions;
        filters.forEach((filter) => {
            // count the entries that match the current filter option
            const counter = allEntries.filter((entry: T) =>
                entry.features.find((feature) => feature.description === filterKey && feature.value === filter.name)
            ).length;
            // if there are no entries that match the current filter then set the isSelected flag to false
            if (counter === 0) {
                filter.isSelected = false;
            }
            // update the number of entries
            filter.numberOfItems = counter;
        });
    }

    if (callback) {
        callback(clonedFilters);
    }
}
